import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import {
  User as FirebaseUser,
  checkActionCode,
  confirmPasswordReset,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { Unsubscribe } from 'firebase/firestore';
import { auth, googleProvider, appleProvider } from '../services/firebase';
import {
  getUserAccountByFirebaseId,
  subscribeToUserAccountByFirebaseId,
  updateUserAccountFields,
} from '../services/users';
import { createNewUserAccount } from '../services/newUserAccount';
import { recordLoginIp } from '../services/functions';
import { UserAccount } from '../types/userAccount';
import { AppRoles, NO_ROLES, deriveRoles, IdTokenClaims } from '../auth/roles';
import { logGlobalError } from '../utils/globalErrorLogger';
import { isDatabaseClosingOrHiddenError } from '../services/errorReporter';

const IMPERSONATED_ACCOUNT_STORAGE_KEY = 'aligned.admin.impersonatedAccount';
const ACCOUNT_RESOLUTION_RETRY_DELAYS_MS = [500, 2_000, 8_000] as const;
// 'internal' is included alongside the transport-level codes: it's Cloud
// Functions' catch-all for an unhandled exception or a malformed response,
// which in practice includes transient cold-start/dependency blips — and a
// bounded, capped retry (see ACCOUNT_RESOLUTION_RETRY_DELAYS_MS) costs little
// even when the failure turns out to be persistent.
const TRANSIENT_ACCOUNT_ERROR_CODES = new Set(['aborted', 'deadline-exceeded', 'unavailable', 'internal']);

function errorCode(error: unknown): string | null {
  if (typeof error !== 'object' || error === null || !('code' in error)) return null;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

/**
 * Account resolution runs immediately after auth and is allowed to create a
 * missing account. Only retry failures that a later network attempt can fix;
 * retrying permission/auth failures just delays the same failure and spams
 * error reporting.
 */
function isTransientAccountResolutionError(error: unknown): boolean {
  const code = errorCode(error);
  if (code === 'permission-denied' || code === 'unauthenticated') return false;
  if (code) return TRANSIENT_ACCOUNT_ERROR_CODES.has(code);
  return error instanceof Error && /connection failed|failed to fetch|network request failed/i.test(error.message);
}

function accountResolutionDelay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function onlineState(): boolean | null {
  return typeof navigator === 'undefined' ? null : navigator.onLine;
}

async function resolveUserAccountId(
  user: FirebaseUser,
  onAttempt: (attempt: number) => void,
  shouldContinue: () => boolean,
): Promise<string | null> {
  for (let attempt = 0; ; attempt += 1) {
    if (!shouldContinue()) return null;
    onAttempt(attempt + 1);
    try {
      const existing = await getUserAccountByFirebaseId(user.uid);
      if (existing) return existing.id;

      // createNewUserAccount is idempotent: its lookup/transaction path finds
      // a record if a previous write succeeded but its response was lost.
      const created = await createNewUserAccount(user);
      return created.userAccountId;
    } catch (err) {
      const isLastRetry = attempt >= ACCOUNT_RESOLUTION_RETRY_DELAYS_MS.length;
      if (!isTransientAccountResolutionError(err) || isLastRetry) throw err;
      await accountResolutionDelay(ACCOUNT_RESOLUTION_RETRY_DELAYS_MS[attempt]);
      if (!shouldContinue()) return null;
    }
  }
}

interface AuthContextType extends AppRoles {
  currentUser: FirebaseUser | null;
  userAccount: UserAccount | null;
  /** The account currently shown in the UI when an admin is impersonating. */
  impersonatedAccount: UserAccount | null;
  isImpersonating: boolean;
  loading: boolean;
  /** Signed in to Firebase but no matching user_accounts doc (e.g. brand-new
   *  social login before account creation, which requires account creation). */
  accountMissing: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  /** Validate a password-reset oobCode; resolves the associated email. */
  validateResetCode: (oobCode: string) => Promise<string>;
  /** Complete a password reset with an oobCode + new password. */
  confirmReset: (oobCode: string, newPassword: string) => Promise<void>;
  impersonateUser: (account: UserAccount) => Promise<void>;
  reverseImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userAccount, setUserAccount] = useState<UserAccount | null>(null);
  const [impersonatedAccount, setImpersonatedAccount] = useState<UserAccount | null>(() => {
    try {
      if (typeof window === 'undefined') return null;
      const stored = window.localStorage.getItem(IMPERSONATED_ACCOUNT_STORAGE_KEY);
      return stored ? (JSON.parse(stored) as UserAccount) : null;
    } catch {
      return null;
    }
  });
  const [roles, setRoles] = useState<AppRoles>(NO_ROLES);
  const [loading, setLoading] = useState(true);
  const seqRef = useRef(0);

  function signIn(email: string, password: string) {
    return signInWithEmailAndPassword(auth, email, password).then(() => undefined);
  }
  function signInWithGoogle() {
    return signInWithPopup(auth, googleProvider).then(() => undefined);
  }
  function signInWithApple() {
    return signInWithPopup(auth, appleProvider).then(() => undefined);
  }
  function logout() {
    return signOut(auth);
  }
  function resetPassword(email: string) {
    // `actionCodeSettings` points the emailed link back at this app's own
    // /password-reset route (matching aligned's `sendCustomPasswordResetEmail`)
    // instead of Firebase's generic hosted reset page.
    return sendPasswordResetEmail(auth, email, {
      url: `${window.location.origin}/password-reset`,
      handleCodeInApp: true,
    });
  }
  async function validateResetCode(oobCode: string): Promise<string> {
    const info = await checkActionCode(auth, oobCode.trim());
    const email = info.data.email;
    if (!email) throw new Error('This reset link is invalid.');
    return email;
  }
  function confirmReset(oobCode: string, newPassword: string) {
    return confirmPasswordReset(auth, oobCode.trim(), newPassword);
  }

  async function impersonateUser(account: UserAccount): Promise<void> {
    setImpersonatedAccount(account);
    try {
      window.localStorage.setItem(IMPERSONATED_ACCOUNT_STORAGE_KEY, JSON.stringify(account));
    } catch {
      // Local persistence is best-effort; the in-memory override still works.
    }
  }

  async function reverseImpersonation(): Promise<void> {
    setImpersonatedAccount(null);
    try {
      window.localStorage.removeItem(IMPERSONATED_ACCOUNT_STORAGE_KEY);
    } catch {
      // Ignore storage failures while restoring the real account.
    }
    if (currentUser) {
      const restored = await getUserAccountByFirebaseId(currentUser.uid);
      if (restored) setUserAccount(restored);
    }
  }

  useEffect(() => {
    let mounted = true;
    let accountUnsub: Unsubscribe | null = null;

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const seq = seqRef.current + 1;
      seqRef.current = seq;
      if (accountUnsub) {
        accountUnsub();
        accountUnsub = null;
      }

      setCurrentUser(user);
      setLoading(true);

      if (!user) {
        if (mounted && seqRef.current === seq) {
          setImpersonatedAccount(null);
          try {
            window.localStorage.removeItem(IMPERSONATED_ACCOUNT_STORAGE_KEY);
          } catch {
            // Ignore storage failures while signing out.
          }
          setUserAccount(null);
          setRoles(NO_ROLES);
          setLoading(false);
        }
        return;
      }

      const start = async () => {
        // Force-refresh once so custom claims (esp. isManager, computed
        // server-side) are current for this session.
        let claims: IdTokenClaims = {};
        try {
          const token = await user.getIdTokenResult(true);
          claims = token.claims as IdTokenClaims;
        } catch (err) {
          // The IndexedDB connection Firebase Auth persists to can be mid-close
          // if the tab just became hidden or entered bfcache — a one-shot retry
          // clears it almost immediately once the write settles.
          if (isDatabaseClosingOrHiddenError(err)) {
            try {
              const token = await user.getIdTokenResult(true);
              claims = token.claims as IdTokenClaims;
            } catch (retryErr) {
              logGlobalError(retryErr, {
                context: 'AuthContext.getIdTokenResult',
                severity: 'WARNING',
                metadata: { uid: user.uid, retried: true },
              });
            }
          } else {
            logGlobalError(err, {
              context: 'AuthContext.getIdTokenResult',
              metadata: { uid: user.uid },
            });
          }
        }
        if (!mounted || seqRef.current !== seq) return;

        // First sign-in (incl. social Google/Apple) may have no account doc yet.
        // Create/link it before subscribing so the user lands in the app rather
        // than /no-permissions. Idempotent — createNewUserAccount resolves an
        // existing account by email/firebaseId. Email/password signup creates the
        // account itself; this is the safety net + the social path.
        let resolvedUserAccountId: string | null = null;
        let accountResolutionAttempts = 0;
        try {
          resolvedUserAccountId = await resolveUserAccountId(
            user,
            (attempt) => {
              accountResolutionAttempts = attempt;
            },
            () => mounted && seqRef.current === seq,
          );
        } catch (err) {
          logGlobalError(err, {
            context: 'AuthContext.ensureAccount',
            metadata: {
              uid: user.uid,
              attempt: accountResolutionAttempts,
              errorCode: errorCode(err),
              online: onlineState(),
            },
          });
        }
        if (!mounted || seqRef.current !== seq) return;

        // Keep the public account audit field current for admin search. This
        // is a targeted, best-effort write so a stale timestamp never blocks
        // an otherwise valid login.
        if (resolvedUserAccountId) {
          void updateUserAccountFields(resolvedUserAccountId, {
            lastSignInDate: new Date().toISOString(),
          }).catch((err: unknown) => {
            logGlobalError(err, {
              context: 'AuthContext.updateLastSignInDate',
              metadata: { uid: user.uid, userAccountId: resolvedUserAccountId },
            });
          });
        }

        // Record both fresh logins and restored sessions when the app is
        // opened. This is intentionally best-effort: a failed audit write must
        // never prevent an otherwise valid user from entering the app.
        void recordLoginIp().catch((err: unknown) => {
          logGlobalError(err, {
            context: 'AuthContext.recordLoginIp',
            metadata: { uid: user.uid },
            // Best-effort audit write (see comment above) — a timeout or
            // transport failure here isn't a defect, so keep it out of Error
            // Reporting while still logging it for observability.
            severity: 'WARNING',
          });
        });

        // Live-subscribe so role flips on the account doc take effect without
        // requiring a sign-out.
        accountUnsub = subscribeToUserAccountByFirebaseId(user.uid, (account, metadata) => {
          if (!mounted || seqRef.current !== seq) return;
          // Firestore can emit an empty cache snapshot before the server
          // responds after login. It is not evidence that the account is
          // missing; keep the route guard in its loading state until the
          // authoritative snapshot arrives.
          if (!account && metadata.fromCache) return;
          setUserAccount(account);
          setRoles(deriveRoles(account, claims));
          setLoading(false);
        });
      };
      void start();
    });

    return () => {
      mounted = false;
      seqRef.current += 1;
      if (accountUnsub) accountUnsub();
      unsubscribe();
    };
  }, []);

  const value: AuthContextType = {
    currentUser,
    userAccount: impersonatedAccount ?? userAccount,
    impersonatedAccount,
    isImpersonating: impersonatedAccount !== null,
    loading,
    accountMissing: !!currentUser && !loading && userAccount === null,
    ...roles,
    signIn,
    signInWithGoogle,
    signInWithApple,
    logout,
    resetPassword,
    validateResetCode,
    confirmReset,
    impersonateUser,
    reverseImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
